import { pipToDecimal } from '@katanaperps/katana-perps-sdk';
import React, { useMemo } from 'react';
import styled from 'styled-components';

import {
  useVaultOnChainQuantityAvailableForExitWithdrawalQuery,
  useVaultOnChainSummaryQuery,
  useVaultOnChainWithdrawalQueueQuery,
} from 'api/queries/vaultsOnChainQueries';
import DataPair from 'components/basic/panels/DataPair';
import TextColored from 'components/basic/text/TextColored';
import MoreInfoTooltip from 'components/basic/tooltip/MoreInfoTooltip';
import { abbreviateNumber, formatUsdWithSign } from 'lib/display/formatting';

import VaultIconSvg from '../images/VaultIconSvg';
import { calculateVaultTresholds } from '../shared/utils';
import {
  vaultDepositorObligationsDescription,
  vaultDepositsCapDescription,
  vaultExitThresholdDescription,
  vaultExitValueDescription,
  vaultWithdrawalRequestsDescription,
  vaultWithdrawalThresholdDescription,
} from '../shared/vaultSharedTooltips';

const Wrap = styled.div`
  margin: 14px 0 8px;
  padding: 8px;
  border-radius: 2px;
  background: var(--black1b);
`;

const TitleFlex = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  padding: 1px 0 8px;

  color: var(--gray4d);
  font-size: var(--smallest);
  font-style: normal;
  font-weight: normal;
  line-height: 1;
  text-transform: uppercase;
`;

export default function VaultStatsPanel({
  managerWallet,
}: {
  managerWallet: string;
}) {
  const { data: summaryData } = useVaultOnChainSummaryQuery({
    managerWallet,
  });
  const { data: withdrawalQueueData } = useVaultOnChainWithdrawalQueueQuery({
    managerWallet,
  });
  const { data: quantityAvailableForExitWithdrawalData } =
    useVaultOnChainQuantityAvailableForExitWithdrawalQuery({ managerWallet });

  const formatUsdOrDash = (value?: number | string) =>
    value === undefined ? '--' : formatUsdWithSign(Number(value));

  const { maxNetDeposits, depositorNetDeposits, totalOwedQuantity } =
    useMemo(() => {
      return {
        totalOwedQuantity: summaryData
          ? pipToDecimal(summaryData.totalOwedQuantity)
          : undefined,
        depositorNetDeposits: summaryData
          ? pipToDecimal(summaryData.depositorNetDeposits)
          : undefined,
        maxNetDeposits:
          summaryData?.configurationFieldsTransformed.maximumNetDeposits,
      };
    }, [summaryData]);

  const withdrawalRequestsUsd = useMemo(() => {
    if (!withdrawalQueueData) {
      return undefined;
    }
    const sumBN =
      withdrawalQueueData.reduce(
        (sum: bigint, item) => sum + (item?.grossQuantity ?? 0n),
        0n
      ) || 0n;
    return pipToDecimal(sumBN);
  }, [withdrawalQueueData]);

  const { exitThresholdValue, withdrawalThresholdValue } =
    calculateVaultTresholds(summaryData?.configurationFieldsTransformed, {
      totalOwedQuantity: summaryData?.totalOwedQuantity,
    });

  const depositsDivCapPercentage =
    maxNetDeposits && depositorNetDeposits
      ? (Number(depositorNetDeposits) / Number(maxNetDeposits)) * 10000
      : undefined;

  return (
    <Wrap>
      <TitleFlex>
        <VaultIconSvg color="var(--gray4d)" marginTop="-1px" />
        Fixed Income V1
      </TitleFlex>
      <DataPair
        label={
          <MoreInfoTooltip tooltip={vaultDepositorObligationsDescription}>
            Depositor Obligations
          </MoreInfoTooltip>
        }
        noPadding
      >
        {formatUsdOrDash(totalOwedQuantity)}
      </DataPair>
      <DataPair
        label={
          <MoreInfoTooltip tooltip={vaultDepositsCapDescription}>
            Deposits / Cap
          </MoreInfoTooltip>
        }
        noPadding
      >
        <TextColored
          color={
            !depositsDivCapPercentage
              ? 'default'
              : depositsDivCapPercentage > 99
              ? 'yellow'
              : depositsDivCapPercentage > 90
              ? 'yellow'
              : 'default'
          }
        >
          {formatUsdOrDash(depositorNetDeposits)}{' '}
        </TextColored>{' '}
        /{' '}
        {maxNetDeposits
          ? `$${abbreviateNumber(Number(maxNetDeposits), 2)}`
          : '--'}
      </DataPair>
      <DataPair
        label={
          <MoreInfoTooltip tooltip={vaultWithdrawalThresholdDescription}>
            Withdrawal Threshold
          </MoreInfoTooltip>
        }
        noPadding
      >
        {formatUsdOrDash(withdrawalThresholdValue)}
      </DataPair>
      <DataPair
        label={
          <MoreInfoTooltip tooltip={vaultWithdrawalRequestsDescription}>
            Withdrawal Requests
          </MoreInfoTooltip>
        }
        noPadding
      >
        <TextColored
          color={
            withdrawalRequestsUsd &&
            quantityAvailableForExitWithdrawalData?.quantityInDecimal &&
            Number(withdrawalRequestsUsd) >
              Number(quantityAvailableForExitWithdrawalData.quantityInDecimal)
              ? 'red'
              : 'default'
          }
        >
          {formatUsdOrDash(withdrawalRequestsUsd)}
        </TextColored>
      </DataPair>
      <DataPair
        label={
          <MoreInfoTooltip tooltip={vaultExitThresholdDescription}>
            Exit Threshold
          </MoreInfoTooltip>
        }
        noPadding
      >
        {exitThresholdValue === undefined
          ? '--'
          : formatUsdWithSign(exitThresholdValue)}
      </DataPair>
      <DataPair
        label={
          <MoreInfoTooltip tooltip={vaultExitValueDescription}>
            Exit Value
          </MoreInfoTooltip>
        }
        noPadding
      >
        <TextColored
          color={
            quantityAvailableForExitWithdrawalData?.quantityInDecimal &&
            exitThresholdValue !== undefined &&
            Number(quantityAvailableForExitWithdrawalData.quantityInDecimal) <
              exitThresholdValue * 1.1 // < 110% of Exit Threshold
              ? 'red'
              : 'default'
          }
        >
          {quantityAvailableForExitWithdrawalData
            ? formatUsdWithSign(
                quantityAvailableForExitWithdrawalData.quantityInDecimal
              )
            : '--'}
        </TextColored>
      </DataPair>
    </Wrap>
  );
}
